import { Box, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, useMediaQuery } from '@mui/material'
import { useParams, useSearchParams } from 'react-router-dom'
import { Reducer, useCallback, useContext, useEffect, useReducer } from 'react'

// others
import { ActionType } from '../context/AppContextReducer'
import { useDebounce } from 'use-debounce'
import { GRAPHQL_QUERY_POLICY, QUERY_PARAMS } from '../constants'
import { AppContext, AuthContext } from '../context'
import { ExternalUserRoleRole, OrderExternalUsersBy, useFetchAllExternalUsersLazyQuery } from '../../generated'

// component import
import { CardBox } from '../theme/styleComponents'
import { ContentHead } from '../components/common/ContentHead'
import { AddNewJudge } from '../components/cases/createNewCase/AddNewJudge'
import { AddNewClient } from '../components/cases/createNewCase/basicDetails/AddNewClient'
import { AddNewAdversary } from '../components/cases/createNewCase/basicDetails/AddNewAdversary'
import { SearchWithKeypress } from '../components/common/SearchWithKeypress'
import { ExternalUsersListing } from '../components/externalUsers/ExternalUserListing'
import {
  ExternalUserAction,
  ExternalUserActionType,
  externalUserInitialState,
  externalUserReducer,
  ExternalUserState
} from '../components/externalUsers/externalUserReducer'
import AddContact from '../components/externalUsers/AddContact'
import { anchorOrigin, transformOrigin } from '../theme/styleConstants'

export const ContactsContainer = ({ isClient }: { isClient?: boolean }) => {
  const { dispatch: reducerDispatch, pageLimit, currentPage, searchValue } = useContext(AppContext)
  const [debouncedSearchTerm] = useDebounce(searchValue, 500)

  const [externalUserState, externalUserDispatch] = useReducer<Reducer<ExternalUserState, ExternalUserAction>>(
    externalUserReducer,
    externalUserInitialState
  )
  const { currentUser, isAccountant, isStaff } = useContext(AuthContext)
  const { type } = useParams()

  const mediumViewport = useMediaQuery('(max-width:1138px)')
  const upperCaseType = isClient ? ExternalUserRoleRole.Client.toLocaleUpperCase() : type?.toLocaleUpperCase()

  const [searchParams, setSearchParams] = useSearchParams()
  const orderByParam = searchParams.get(QUERY_PARAMS.ORDER_BY)
  const orderDir = searchParams.get(QUERY_PARAMS.ORDER_DIRECTION)

  const commonFilterSteps = () => {
    reducerDispatch({ type: ActionType.SET_SEARCH_VALUE, searchValue: '' })
    reducerDispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: 1 })
    searchParams.set('currentPage', currentPage.toString())
  }

  const handleSortOrderChange = (event: SelectChangeEvent) => {
    const { value } = event.target
    commonFilterSteps()
    searchParams.set(QUERY_PARAMS.ORDER_BY, value)
    setSearchParams(searchParams)
  }

  const handleSortDirectionChange = (event: SelectChangeEvent) => {
    const { value } = event.target
    commonFilterSteps()
    searchParams.set(QUERY_PARAMS.ORDER_DIRECTION, value)
    setSearchParams(searchParams)
  }

  const canAddNewContact = (): boolean => {
    return !isAccountant && !isStaff
  }

  const getExternalUserSettings = useCallback(() => {
    switch (upperCaseType) {
      case ExternalUserRoleRole.Adversary:
        return {
          heading: 'Adversaries',
          buttonText: 'Add Adversary',
          empty: 'No Adversaries Found',
          role: [ExternalUserRoleRole.Adversary],
          modal: <AddNewAdversary refetch={refetch} />
        }
      case ExternalUserRoleRole.Judge:
        return {
          heading: 'Judges',
          buttonText: 'Add Judge',
          empty: 'No Judges Found',
          role: [ExternalUserRoleRole.Judge],
          modal: <AddNewJudge refetch={refetch} />
        }
      case ExternalUserRoleRole.Contact:
        return {
          heading: 'Contacts',
          buttonText: 'Add Contact',
          empty: 'No Contact Found',
          role: [ExternalUserRoleRole.Contact],
          modal: <AddContact refetch={refetch} />
        }
      default:
        return {
          heading: 'Clients',
          buttonText: 'Add Client',
          empty: 'No Clients Found',
          role: [ExternalUserRoleRole.Client],
          modal: <AddNewClient refetch={refetch} externalUserState={externalUserState} />
        }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upperCaseType])

  const [fetchAllExternalUsers, { loading, refetch }] = useFetchAllExternalUsersLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,

    onError() {
      reducerDispatch({ type: ActionType.SET_IS_LOADING, isLoading: false })
      externalUserDispatch({ type: ExternalUserActionType.SET_ALL_EXTERNAL_USERS, allExternalUsers: [] })
    },

    onCompleted(data) {
      const {
        fetchAllExternalUsers: { externalUsers, pagination }
      } = data || {}

      if (externalUsers) {
        externalUserDispatch({
          type: ExternalUserActionType.SET_ALL_EXTERNAL_USERS,
          allExternalUsers: externalUsers || []
        })
      }

      if (pagination) {
        const { totalPages } = pagination

        totalPages && reducerDispatch({ type: ActionType.SET_TOTAL_PAGE_COUNT, totalPageCount: totalPages })
      }
    }
  })

  useEffect(() => {
    fetchAllExternalUsers({
      variables: {
        externalUsersInput: {
          roles: getExternalUserSettings().role,
          orderBy: orderByParam
            ? (orderByParam as OrderExternalUsersBy)
            : isClient
            ? OrderExternalUsersBy.ClientId
            : OrderExternalUsersBy.Recent,
          orderByDirection: orderDir ?? 'ASC',
          paginationOptions: {
            page: currentPage,
            limit: pageLimit
          },
          searchValue: debouncedSearchTerm
        }
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, currentPage, debouncedSearchTerm, getExternalUserSettings, orderByParam, orderDir])

  useEffect(() => {
    return () => {
      reducerDispatch({ type: ActionType.SET_SEARCH_VALUE, searchValue: '' })
      reducerDispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: 1 })
    }
    // eslint-disable-next-line
  }, [type])

  const { heading, buttonText, empty, modal } = getExternalUserSettings()
  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}></Box>
      <CardBox marginTop={3}>
        <ContentHead title={heading} buttonText={canAddNewContact() ? buttonText : ''} />

        <Box m={2} display="flex" alignItems="flex-start" justifyContent={'space-between'}>
          <SearchWithKeypress
            searchPlaceholder={'Search'}
            infoText={
              isClient
                ? 'You can search by first name, middle name, last name, business name, client #, email, or phone number.'
                : undefined
            }
          />
          <Box display={'flex'} gap={2}>
            <Box>
              <FormControl variant="outlined" fullWidth>
                <InputLabel id="sortBy">Sort By:</InputLabel>
                <Select
                  labelId="sortBy"
                  id="sortBy"
                  label={'Sort By:'}
                  onChange={handleSortOrderChange}
                  displayEmpty={true}
                  value={
                    searchParams.get(QUERY_PARAMS.ORDER_BY) ||
                    (isClient ? OrderExternalUsersBy.ClientId : OrderExternalUsersBy.Recent)
                  }
                  sx={{ width: mediumViewport ? 262 : 130 }}
                  MenuProps={{
                    anchorOrigin,
                    transformOrigin,
                    style: {
                      maxHeight: 400
                    }
                  }}
                >
                  {isClient && <MenuItem value={OrderExternalUsersBy.ClientId}>Client #</MenuItem>}
                  <MenuItem value={OrderExternalUsersBy.Recent}>Recent</MenuItem>
                  <MenuItem value={OrderExternalUsersBy.FirstName}>First Name</MenuItem>
                  <MenuItem value={OrderExternalUsersBy.LastName}>Last Name</MenuItem>
                  <MenuItem value={OrderExternalUsersBy.Phone}>Phone</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box>
              <FormControl variant="outlined" fullWidth>
                <InputLabel id="sortByDir">A/D</InputLabel>
                <Select
                  labelId="A/D"
                  id="sortByDir"
                  label={'A/D'}
                  onChange={handleSortDirectionChange}
                  displayEmpty={true}
                  value={searchParams.get(QUERY_PARAMS.ORDER_DIRECTION) ?? 'ASC'}
                  sx={{ width: mediumViewport ? 262 : 130 }}
                  MenuProps={{
                    anchorOrigin,
                    transformOrigin,
                    style: {
                      maxHeight: 400
                    }
                  }}
                >
                  <MenuItem value={'ASC'}>ASC</MenuItem>
                  <MenuItem value={'DESC'}>DESC</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
        </Box>

        <ExternalUsersListing
          state={externalUserState}
          dispatch={externalUserDispatch}
          loading={loading}
          refetch={refetch}
          emptyMessage={empty}
          roleType={upperCaseType || ExternalUserRoleRole.Client}
        />
      </CardBox>

      {modal}
    </>
  )
}
